/**
 * The design system's file picker (`.file-picker`): a drop zone that matches the page instead of
 * the browser's bare file control. Underneath it is the same native `<input type="file">` (same
 * id, name, accept, required, aria-describedby), stretched invisibly over the zone: clicking,
 * dropping a file, the keyboard (Tab, then Space/Enter) and the form's submit all work as before.
 * Once a file is chosen the zone shows its name and size, and «Quitar» clears it.
 *
 * Standalone (no console imports, no icon sprite, no inline styles) so the public pages use it
 * too. Usage: `filePicker({...})` in the markup, labelled by the field's own `<label for>`, and
 * `bindFilePickers()` once per page. The API is deliberately small so the control can later be
 * swapped for another implementation (issue #189) without touching the call sites.
 */

export interface FilePickerOptions {
  /** The input's id (the field's `<label for>` points at it). */
  id: string;
  /** The form field name, when the page reads the form with FormData. */
  name?: string;
  /** The `accept` list, as on a native input. */
  accept: string;
  /** Id(s) of the hint/error that describe the input. */
  describedBy?: string;
  required?: boolean;
  /** Extra attributes for the input (already escaped by the caller), e.g. `data-mat-file`. */
  attrs?: string;
  /** What the zone says it takes: «PDF, JPG o PNG · hasta 5 MB». */
  hint?: string;
}

const esc = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Sizes as the console says them: «300 KB», «1.2 MB». */
export const fileSizeText = (n: number) =>
  n < 1024 * 1024
    ? `${Math.max(1, Math.round(n / 1024))} KB`
    : `${(n / 1024 / 1024).toFixed(1)} MB`;

const svg = (paths: string) =>
  `<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${paths}</svg>`;
const UPLOAD = svg('<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>');
const FILE = svg(
  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/>',
);

export function filePicker(o: FilePickerOptions): string {
  const attrs = [
    `id="${esc(o.id)}"`,
    o.name ? `name="${esc(o.name)}"` : '',
    `accept="${esc(o.accept)}"`,
    o.describedBy ? `aria-describedby="${esc(o.describedBy)}"` : '',
    o.required ? 'required' : '',
    o.attrs ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return `<div class="file-picker" data-component="file-picker" data-state="empty">
    <div class="file-picker-zone">
      <input class="file-picker-input" type="file" ${attrs} />
      <div class="file-picker-face" aria-hidden="true">
        <span class="file-picker-empty">${UPLOAD}<span><strong>Elegir archivo</strong><span class="file-picker-drag"> o arrástralo aquí</span>${
          o.hint ? `<small>${esc(o.hint)}</small>` : ''
        }</span></span>
        <span class="file-picker-chosen" hidden>${FILE}<span class="file-picker-name" data-file-name></span><span class="file-picker-size" data-file-size></span><span class="file-picker-change">Cambiar</span></span>
      </div>
    </div>
    <button type="button" class="btn sm file-picker-remove" data-file-remove hidden>Quitar</button>
    <span class="sr-only" aria-live="polite" data-file-status></span>
  </div>`;
}

/** Paints the picker for the input's current file (none, or its name and size). */
function paint(input: HTMLInputElement) {
  const picker = input.closest<HTMLElement>('[data-component="file-picker"]');
  if (!picker) return;
  const file = input.files?.[0];
  const q = <T extends HTMLElement>(sel: string) => picker.querySelector<T>(sel);
  picker.dataset.state = file ? 'chosen' : 'empty';
  const empty = q('.file-picker-empty');
  const chosen = q('.file-picker-chosen');
  const remove = q<HTMLButtonElement>('[data-file-remove]');
  if (empty) empty.hidden = !!file;
  if (chosen) chosen.hidden = !file;
  if (remove) {
    remove.hidden = !file;
    if (file) remove.setAttribute('aria-label', `Quitar ${file.name}`);
    else remove.removeAttribute('aria-label');
  }
  const name = q('[data-file-name]');
  const size = q('[data-file-size]');
  if (name) name.textContent = file?.name ?? '';
  if (size) size.textContent = file ? fileSizeText(file.size) : '';
  const status = q('[data-file-status]');
  if (status)
    status.textContent = file
      ? `Archivo elegido: ${file.name}, ${fileSizeText(file.size)}.`
      : 'Ningún archivo elegido.';
  if (file) input.removeAttribute('aria-invalid');
}

const isPickerInput = (t: EventTarget | null): t is HTMLInputElement =>
  t instanceof HTMLElement && t.matches('.file-picker-input');

const bound = new WeakSet<object>();

/**
 * Listens (once per document or root, by delegation, so pickers painted later work too) to the
 * pickers' changes, «Quitar» and drags over the zone.
 */
export function bindFilePickers(root: Document | HTMLElement = document) {
  if (bound.has(root)) return;
  bound.add(root);
  root.addEventListener('change', (e) => {
    if (isPickerInput(e.target)) paint(e.target);
  });
  root.addEventListener('click', (e) => {
    const button = (e.target as HTMLElement | null)?.closest?.('[data-file-remove]');
    if (!button) return;
    const input = button
      .closest('[data-component="file-picker"]')
      ?.querySelector<HTMLInputElement>('.file-picker-input');
    if (!input) return;
    input.value = '';
    // The page reacts to the change as if the person had cleared it in the dialog.
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.focus();
  });
  for (const type of ['dragenter', 'dragleave', 'drop'] as const)
    root.addEventListener(type, (e) => {
      if (!isPickerInput(e.target)) return;
      e.target.closest('.file-picker-zone')?.classList.toggle('is-dragging', type === 'dragenter');
    });
}

/** A file sent as soon as it is chosen («Subir informe», «Tomar foto»): a button, not a zone. */
export interface FileButtonOptions {
  /** The input's id: the button is a `<label for>` beside it. Without one, the input goes inside. */
  id?: string;
  /** What the button says (HTML, already escaped by the caller: an icon and a verb). */
  label: string;
  accept: string;
  /** The camera a phone opens directly (`user` or `environment`). */
  capture?: 'user' | 'environment';
  /** Extra attributes for the input (already escaped by the caller), e.g. `data-uw-doc="…"`. */
  attrs?: string;
  /** The button's classes (`btn sm file-btn` by default). */
  className?: string;
}

/**
 * The design system's other file control: a `.btn.file-btn` with the native input, visually
 * hidden, so the browser's bare file control never shows. The page listens to its `change`.
 */
export function fileButton(o: FileButtonOptions): string {
  const cls = esc(o.className ?? 'btn sm file-btn');
  const input = `<input class="sr-only" type="file"${o.id ? ` id="${esc(o.id)}"` : ''} accept="${esc(o.accept)}"${
    o.capture ? ` capture="${o.capture}"` : ''
  }${o.attrs ? ` ${o.attrs}` : ''} />`;
  return o.id
    ? `<label class="${cls}" for="${esc(o.id)}">${o.label}</label>${input}`
    : `<label class="${cls}">${o.label}${input}</label>`;
}
